import React, { useState } from 'react';
import { arrayOf, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import { Link } from 'react-router-dom';

import { FormattedMessage } from '../../../util/reactIntl';
import { getCategories } from '../../../config/runtimeListingConfig';
import { getCategoryUrl } from '../../../util/categoryLandingHelpers';

import css from './ShopByCategoryColumn.module.css';

/**
 * "Shop by Category" column (browse redesign Phase 3, ledger §1).
 *
 * Pure navigation — every row is a real page load to a category landing URL.
 * Anatomy on any tree shape: ancestor back-links, the current category as a
 * wash-tinted row with its total, its children (counts muted right; a "›"
 * marks deeper levels; empties muted with "none yet", collapsed behind a
 * disclosure when there are many), then a discovery tier of siblings
 * ("Nearby in {parent}", or "Other families" at the top level).
 *
 * treeCounts is { categoryId: rolled-up count } or null when the counts
 * endpoint degraded — with null the column renders all rows without numbers
 * and mutes nothing (better to overpromise than wrongly grey out).
 */

// Above this many empty children they collapse behind the disclosure row
const INLINE_EMPTY_LIMIT = 3;

const countOf = (treeCounts, id) => (treeCounts ? treeCounts[id] || 0 : null);

const CategoryRowLink = ({ category, treeCounts, showDeeperMark, className }) => {
  const count = countOf(treeCounts, category.id);
  const isEmpty = count === 0;
  const hasChildren = category.subcategories?.length > 0;
  return (
    <Link
      to={getCategoryUrl(category.id)}
      className={classNames(css.row, className, { [css.rowEmpty]: isEmpty })}
    >
      <span className={css.rowName}>{category.name}</span>
      <span className={css.rowCount}>
        {isEmpty ? (
          <FormattedMessage id="ShopByCategoryColumn.noneYet" />
        ) : count != null ? (
          <>
            {count}
            {showDeeperMark && hasChildren ? <span className={css.deeperMark}> ›</span> : null}
          </>
        ) : showDeeperMark && hasChildren ? (
          <span className={css.deeperMark}>›</span>
        ) : null}
      </span>
    </Link>
  );
};

const ShopByCategoryColumn = props => {
  const { className, category, breadcrumbPath, treeCounts } = props;
  const [emptiesExpanded, setEmptiesExpanded] = useState(false);

  if (!category) return null;

  const ancestors = breadcrumbPath.slice(0, -1);
  const parent = ancestors.length > 0 ? ancestors[ancestors.length - 1] : null;
  const children = category.subcategories || [];
  const currentCount = countOf(treeCounts, category.id);

  // With counts available, split children into stocked and empty
  const hasCounts = !!treeCounts;
  const stockedChildren = hasCounts
    ? children.filter(c => countOf(treeCounts, c.id) > 0)
    : children;
  const emptyChildren = hasCounts ? children.filter(c => countOf(treeCounts, c.id) === 0) : [];
  const collapseEmpties = emptyChildren.length > INLINE_EMPTY_LIMIT && !emptiesExpanded;

  // Discovery tier: siblings of the current category; top level shows the
  // other L1 families. Empty siblings stay out of the tier.
  const siblingSource = parent ? parent.subcategories || [] : getCategories();
  const siblings = siblingSource
    .filter(c => c.id !== category.id)
    .filter(c => !hasCounts || countOf(treeCounts, c.id) > 0);

  return (
    <nav className={classNames(css.root, className)}>
      <div className={css.label}>
        <FormattedMessage id="ShopByCategoryColumn.label" />
      </div>

      {ancestors.map(ancestor => (
        <Link key={ancestor.id} to={getCategoryUrl(ancestor.id)} className={css.ancestorRow}>
          <span className={css.backMark}>‹</span> {ancestor.name}
        </Link>
      ))}

      <div className={css.currentRow}>
        <span className={css.rowName}>{category.name}</span>
        {currentCount != null ? <span className={css.currentCount}>{currentCount}</span> : null}
      </div>

      {stockedChildren.map(child => (
        <CategoryRowLink
          key={child.id}
          category={child}
          treeCounts={treeCounts}
          showDeeperMark
          className={css.childRow}
        />
      ))}

      {collapseEmpties ? (
        <button
          type="button"
          className={css.disclosure}
          onClick={() => setEmptiesExpanded(true)}
        >
          <FormattedMessage
            id="ShopByCategoryColumn.moreWithNothingListed"
            values={{ count: emptyChildren.length }}
          />
        </button>
      ) : (
        emptyChildren.map(child => (
          <CategoryRowLink
            key={child.id}
            category={child}
            treeCounts={treeCounts}
            className={css.childRow}
          />
        ))
      )}

      {siblings.length > 0 ? (
        <div className={css.tierBlock}>
          <div className={classNames(css.label, css.tierLabel)}>
            {parent ? (
              <FormattedMessage
                id="ShopByCategoryColumn.nearbyIn"
                values={{ parentName: parent.name }}
              />
            ) : (
              <FormattedMessage id="ShopByCategoryColumn.otherFamilies" />
            )}
          </div>
          {siblings.map(sibling => (
            <CategoryRowLink
              key={sibling.id}
              category={sibling}
              treeCounts={treeCounts}
              className={css.tierRow}
            />
          ))}
        </div>
      ) : null}
    </nav>
  );
};

ShopByCategoryColumn.defaultProps = {
  className: null,
  treeCounts: null,
};

ShopByCategoryColumn.propTypes = {
  className: string,
  category: object.isRequired,
  breadcrumbPath: arrayOf(shape({ id: string, name: string })).isRequired,
  treeCounts: object,
};

export default ShopByCategoryColumn;
